import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Loader2, Save, X, Server, Globe, Mail, Upload, Terminal, Pencil } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../../config';

const TYPE_OPTIONS = [
  { value: 'manual', label: 'Saisie manuelle', icon: Pencil, desc: 'Prix saisis a la main' },
  { value: 'csv', label: 'CSV / Excel', icon: Upload, desc: 'Import de fichier' },
  { value: 'ftp', label: 'FTP', icon: Server, desc: 'Fichier sur serveur FTP' },
  { value: 'api', label: 'API', icon: Terminal, desc: 'Endpoint REST' },
  { value: 'web', label: 'Portail web', icon: Globe, desc: 'Scraping site web' },
  { value: 'email', label: 'Email / Excel', icon: Mail, desc: 'Fichier recu par mail' },
];

const EMPTY_SUPPLIER = {
  name: '', type: 'manual', url: '', notes: '', active: true,
  credentials: { username: '', password: '', api_key: '' },
  ftp_config: { host: '', port: 21, path: '/' },
  default_margin_pct: 25,
};

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or supplier object
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data.suppliers || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const handleCredChange = (key, value) => setForm(prev => ({ ...prev, credentials: { ...prev.credentials, [key]: value } }));
  const handleFtpChange = (key, value) => setForm(prev => ({ ...prev, ftp_config: { ...prev.ftp_config, [key]: value } }));

  const startEdit = (supplier) => {
    setEditing(supplier.supplier_id);
    setForm({
      name: supplier.name || '',
      type: supplier.type || 'manual',
      url: supplier.url || '',
      notes: supplier.notes || '',
      active: supplier.active !== false,
      credentials: { username: '', password: '', api_key: '', ...(supplier.credentials || {}) },
      ftp_config: { host: '', port: 21, path: '/', ...(supplier.ftp_config || {}) },
      default_margin_pct: supplier.default_margin_pct ?? 25,
    });
  };

  const startCreate = () => {
    setEditing('new');
    setForm(EMPTY_SUPPLIER);
  };

  const cancel = () => { setEditing(null); setForm(EMPTY_SUPPLIER); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing === 'new') {
        await createSupplier(form);
      } else {
        await updateSupplier(editing, form);
      }
      setEditing(null);
      setForm(EMPTY_SUPPLIER);
      await load();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur et tous ses prix ?')) return;
    try {
      await deleteSupplier(id);
      await load();
    } catch { /* ignore */ }
  };

  if (loading) return (
    <AdminLayout title="Fournisseurs">
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-secondary animate-spin" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Fournisseurs">
      <div className="max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-slate-600">{suppliers.length} fournisseur(s) configure(s)</p>
          <button onClick={startCreate}
            className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-5 py-2.5 text-sm transition-colors">
            <Plus className="w-4 h-4" /> Ajouter un fournisseur
          </button>
        </div>

        {/* Edit/Create form */}
        {editing && (
          <div className="bg-white rounded-sm border border-secondary/30 p-6 mb-6 space-y-4">
            <h3 className="font-heading font-semibold text-primary">
              {editing === 'new' ? 'Nouveau fournisseur' : 'Modifier le fournisseur'}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Nom</label>
                <input type="text" value={form.name} onChange={e => handleChange('name', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  placeholder="Ex: TD Synnex" required />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Marge par defaut (%)</label>
                <input type="number" step="0.1" min="0" max="100" value={form.default_margin_pct}
                  onChange={e => handleChange('default_margin_pct', parseFloat(e.target.value) || 0)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none" />
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-2">Type de connexion</label>
              <div className="grid grid-cols-3 gap-2">
                {TYPE_OPTIONS.map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.value} type="button" onClick={() => handleChange('type', t.value)}
                      className={`flex items-center gap-2 p-3 rounded-sm border text-left text-xs transition-colors ${
                        form.type === t.value
                          ? 'border-secondary bg-secondary/10 text-secondary'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}>
                      <Icon className="w-4 h-4 shrink-0" />
                      <div>
                        <div className="font-medium">{t.label}</div>
                        <div className="text-[10px] text-slate-500">{t.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* URL (for api, web) */}
            {['api', 'web'].includes(form.type) && (
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">URL</label>
                <input type="url" value={form.url} onChange={e => handleChange('url', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                  placeholder="https://..." />
              </div>
            )}

            {/* Credentials (for api, web, ftp) */}
            {['api', 'web', 'ftp'].includes(form.type) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Identifiant</label>
                  <input type="text" value={form.credentials.username} onChange={e => handleCredChange('username', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Mot de passe / Cle API</label>
                  <input type="password" value={form.credentials.password || form.credentials.api_key}
                    onChange={e => handleCredChange(form.type === 'api' ? 'api_key' : 'password', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none" />
                </div>
              </div>
            )}

            {/* FTP config */}
            {form.type === 'ftp' && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Hote FTP</label>
                  <input type="text" value={form.ftp_config.host} onChange={e => handleFtpChange('host', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                    placeholder="ftp.example.com" />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Port</label>
                  <input type="number" value={form.ftp_config.port} onChange={e => handleFtpChange('port', parseInt(e.target.value) || 21)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Chemin</label>
                  <input type="text" value={form.ftp_config.path} onChange={e => handleFtpChange('path', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none"
                    placeholder="/catalogue/" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs text-slate-600 mb-1.5">Notes</label>
              <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} rows={2}
                className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none resize-none"
                placeholder="Informations complementaires..." />
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => handleChange('active', e.target.checked)}
                  className="rounded bg-slate-50 border-slate-300 text-secondary focus:ring-secondary" />
                Actif
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || !form.name}
                className="bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-6 py-2.5 text-sm transition-colors disabled:opacity-50 flex items-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editing === 'new' ? 'Creer' : 'Enregistrer'}
              </button>
              <button onClick={cancel}
                className="bg-slate-100 text-primary font-medium rounded-full px-5 py-2.5 text-sm hover:bg-white/20 transition-colors">
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Suppliers list */}
        <div className="space-y-3">
          {suppliers.map(s => {
            const typeInfo = TYPE_OPTIONS.find(t => t.value === s.type) || TYPE_OPTIONS[0];
            const Icon = typeInfo.icon;
            return (
              <div key={s.supplier_id} className={`bg-white rounded-sm border p-4 flex items-center gap-4 ${s.active ? 'border-slate-200' : 'border-slate-200 opacity-50'}`}>
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${s.active ? 'bg-secondary/10' : 'bg-slate-100'}`}>
                  <Icon className={`w-5 h-5 ${s.active ? 'text-secondary' : 'text-slate-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-primary truncate">{s.name}</p>
                    {!s.active && <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">Inactif</span>}
                  </div>
                  <p className="text-xs text-slate-500">{typeInfo.label} — Marge: {s.default_margin_pct ?? 25}%</p>
                  {s.notes && <p className="text-xs text-gray-600 truncate mt-0.5">{s.notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => startEdit(s)}
                    className="w-8 h-8 rounded-sm bg-slate-100 flex items-center justify-center hover:bg-slate-100 transition-colors">
                    <Edit2 className="w-3.5 h-3.5 text-slate-600" />
                  </button>
                  <button onClick={() => handleDelete(s.supplier_id)}
                    className="w-8 h-8 rounded-sm bg-slate-100 flex items-center justify-center hover:bg-rose-100 transition-colors">
                    <Trash2 className="w-3.5 h-3.5 text-slate-600" />
                  </button>
                </div>
              </div>
            );
          })}

          {suppliers.length === 0 && !editing && (
            <div className="text-center py-12 text-slate-500">
              <p className="text-sm">Aucun fournisseur configure</p>
              <p className="text-xs mt-1">Cliquez "Ajouter un fournisseur" pour commencer</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
